import classNames from "classnames";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface Explanation {
	title?: string;
	lines: (string | false | null | undefined)[];
}

type Align = "center" | "left" | "right";

interface Props {
	explain: Explanation;
	children: React.ReactNode;
	className?: string;
	align?: Align;
	block?: boolean;
}

const GAP = 8;
const EDGE = 12;

const Hint = ({ explain, children, className, align = "center", block = false }: Props) => {
	const anchor = useRef<HTMLElement>(null);
	const [target, setTarget] = useState<DOMRect | null>(null);
	const lines = explain.lines.filter(Boolean) as string[];
	const Tag = (block ? "div" : "span") as "span";

	const show = () => {
		if (anchor.current) setTarget(anchor.current.getBoundingClientRect());
	};
	const hide = () => setTarget(null);

	useEffect(() => {
		if (!target) return;
		window.addEventListener("scroll", hide, true);
		window.addEventListener("resize", hide);
		return () => {
			window.removeEventListener("scroll", hide, true);
			window.removeEventListener("resize", hide);
		};
	}, [target]);

	return (
		<Tag
			ref={anchor}
			className={classNames("ed-hint", { "ed-hint--block": block }, className)}
			tabIndex={0}
			onMouseEnter={show}
			onMouseLeave={hide}
			onFocus={show}
			onBlur={hide}
		>
			{children}
			{target &&
				createPortal(
					<Bubble target={target} align={align}>
						{explain.title && <span className="ed-hint__title">{explain.title}</span>}
						{lines.map((line, index) => (
							<span className="ed-hint__line" key={index}>
								{line}
							</span>
						))}
					</Bubble>,
					document.body
				)}
		</Tag>
	);
};

const Bubble = ({ target, align, children }: { target: DOMRect; align: Align; children: React.ReactNode }) => {
	const bubble = useRef<HTMLSpanElement>(null);
	const [position, setPosition] = useState<{ top: number; left: number; below: boolean } | null>(null);

	useLayoutEffect(() => {
		if (!bubble.current) return;
		const { width, height } = bubble.current.getBoundingClientRect();
		const below = target.top - GAP - height < EDGE;
		const top = below ? target.bottom + GAP : target.top - GAP - height;
		const preferred =
			align === "left" ? target.left : align === "right" ? target.right - width : target.left + target.width / 2 - width / 2;
		const left = Math.max(EDGE, Math.min(preferred, window.innerWidth - width - EDGE));
		setPosition({ top, left, below });
	}, [target, align]);

	return (
		<span
			ref={bubble}
			role="tooltip"
			className={classNames("ed-hint__bubble", { "ed-hint__bubble--below": position && position.below })}
			style={position ? { top: position.top, left: position.left } : { top: 0, left: 0, visibility: "hidden" }}
		>
			{children}
		</span>
	);
};

export const InfoDot = ({ explain, align }: { explain: Explanation; align?: Align }) => (
	<Hint explain={explain} align={align} className="ed-info">
		<span className="ed-info__dot" aria-label="How this is calculated">
			i
		</span>
	</Hint>
);

export default Hint;
